// src/pages/program/ProgramHome.jsx

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PROGRAM_DATA, getPhaseData } from '../../data/programData';
import { 
  PlayCircle, 
  Lock, 
  CheckCircle,
  Dumbbell,
  BookOpen,
  KeyRound
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { Button, Input } from '../../components/ui';
import { api } from '../../services/api';

export function ProgramHome() {
  const navigate = useNavigate();
  const { user, aclCare, updateAclCare } = useAuth();

  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [unlockCode, setUnlockCode] = useState('');
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [unlockError, setUnlockError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const phases = Object.values(PROGRAM_DATA.phases);

  const getPhaseStatus = (phaseKey) => {
    const phaseData = aclCare?.phases?.[phaseKey];
    if(!user.patient_id) return 'active'; // All phases active for non-patients
    if (!phaseData) return 'locked';
    if (phaseData.test_out_passed) return 'completed';
    if (phaseData.unlocked) return 'active';
    return 'locked';
  };

  const getPhaseProgress = (phaseKey) => {
    const phaseData = aclCare?.phases?.[phaseKey];
    if (!phaseData?.completions ) return null;
    
    const { completions } = phaseData;
    const completed = completions.session_1 + completions.session_2;
    const total = getPhaseData(phaseKey)?.targetCompletions?.session_1 + getPhaseData(phaseKey)?.targetCompletions?.session_2;
    return { completed, total };
  };

  const handleUnlockClick = (e, phase) => {
    e.stopPropagation();
    setSelectedPhase(phase);
    setUnlockCode('');
    setUnlockError('');
    setShowUnlockModal(true);
  };

  const handleUnlockSubmit = async (e) => {
    e.preventDefault();
    setUnlockLoading(true);
    setUnlockError('');

    const phaseKey = `phase_${selectedPhase.id}`;

    try {
      const result = await api.validateUnlockCode(unlockCode, selectedPhase.id);
      
      if (result.valid) {
        await updateAclCare({
          ...aclCare,
          phases: {
            ...aclCare.phases,
            [phaseKey]: {
              ...aclCare.phases[phaseKey],
              unlocked: true,
              unlocked_at: new Date().toISOString(),
              unlocked_method: 'code',
            }
          },
          unlock_codes_used: [
            ...(aclCare.unlock_codes_used || []),
            {
              code: unlockCode,
              phase: selectedPhase.id,
              used_at: new Date().toISOString(),
              provided_by: null
            }
          ]
        });
        setShowUnlockModal(false);
        setUnlockCode('');
        setSuccessMessage(`${selectedPhase.title} has been unlocked!`);
        setTimeout(() => setSuccessMessage(''), 4000);
      } else {
        setUnlockError('Invalid unlock code. Please try again.');
      }
    } catch (err) {
      setUnlockError(err.message || 'Failed to validate code');
    } finally {
      setUnlockLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowUnlockModal(false);
    setSelectedPhase(null);
    setUnlockCode('');
    setUnlockError('');
  };

  const colorClasses = {
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      icon: 'text-blue-600',
      activeBg: 'bg-blue-100',
    },
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      icon: 'text-green-600',
      activeBg: 'bg-green-100',
    },
    purple: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      icon: 'text-purple-600',
      activeBg: 'bg-purple-100',
    },
    orange: {
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      icon: 'text-orange-600',
      activeBg: 'bg-orange-100',
    },
  };

  return (
    <div className="max-w-2xl mx-auto p-4 pb-24 lg:pb-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Program</h1>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center">
          <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
          <span className="text-green-800 text-sm font-medium">{successMessage}</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        {/* Introduction Tile */}
        <button
          onClick={() => navigate('/program/introduction')}
          className="col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-left hover:shadow-md transition-shadow"
        >
          <div className="flex items-center">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mr-4">
              <PlayCircle className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Introduction</h2>
              <p className="text-sm text-gray-500">Learn about the program</p>
            </div>
          </div>
        </button>

        {/* Phase Tiles */}
        {phases.map((phase) => {
          const status = getPhaseStatus(phase.key);
          const progress = getPhaseProgress(phase.key);
          const colors = colorClasses[phase.color] || colorClasses.blue;
          const isCurrent = aclCare?.current_phase === phase.id;
          const isLocked = status === 'locked';

          const tileContent = (
            <>
              {/* Status Icon */}
              <div className="absolute top-3 right-3">
                {status === 'completed' ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : isLocked ? (
                  <Lock className="h-5 w-5 text-gray-400" />
                ) : null}
              </div>

              {/* Content */}
              <div className={cn(
                "h-10 w-10 rounded-full flex items-center justify-center mb-3",
                isLocked ? 'bg-gray-200' : colors.activeBg
              )}>
                <Dumbbell className={cn(
                  "h-5 w-5",
                  isLocked ? 'text-gray-400' : colors.icon
                )} />
              </div>

              <h3 className="font-semibold text-gray-900">{phase.title}</h3>
              {/* Removing subitle for now
              <p className="text-xs text-gray-500 mt-0.5">{phase.subtitle}</p>
              */}
              {/* Progress indicator */}
              {status === 'active' && progress && (
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Progress</span>
                    <span>{progress.completed}/{progress.total}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className={cn("h-1.5 rounded-full", `bg-${phase.color}-500`)}
                      style={{ width: `${(progress.completed / progress.total) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Unlock button for locked phases */}
              {isLocked && (
                <button
                  onClick={(e) => handleUnlockClick(e, phase)}
                  className="mt-3 flex items-center text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  <KeyRound className="h-3.5 w-3.5 mr-1" />
                  Unlock with code
                </button>
              )}

              {/* Current badge */}
              {isCurrent && (
                <span className="absolute -top-2 left-3 px-2 py-0.5 bg-primary text-white text-xs font-medium rounded-full">
                  Current
                </span>
              )}
            </>
          );

          // Render as div for locked phases (allows button inside), button for unlocked
          if (isLocked) {
            return (
              <div
                key={phase.key}
                className="relative rounded-xl shadow-sm border p-4 text-left bg-gray-50 border-gray-200 opacity-60"
              >
                {tileContent}
              </div>
            );
          }

          return (
            <button
              key={phase.key}
              onClick={() => navigate(`/program/phase/${phase.id}`)}
              className={cn(
                "relative rounded-xl shadow-sm border p-4 text-left transition-all",
                `${colors.bg} ${colors.border} hover:shadow-md cursor-pointer`,
                isCurrent && "ring-2 ring-primary ring-offset-2"
              )}
            >
              {tileContent}
            </button>
          );
        })}

        {/* Exercise Bank Tile */}
        <button
          onClick={() => navigate('/program/exercise-bank')}
          className="col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-left hover:shadow-md transition-shadow"
        >
          <div className="flex items-center">
            <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center mr-4">
              <BookOpen className="h-6 w-6 text-gray-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Exercise Bank</h2>
              <p className="text-sm text-gray-500">Browse all exercises</p>
            </div>
          </div>
        </button>
      </div>

      {/* Unlock Code Modal */}
      {showUnlockModal && selectedPhase && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div 
            className="fixed inset-0 bg-black/50"
            onClick={handleCloseModal}
          />
          
          <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
            <div className="relative bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">
                Unlock {selectedPhase.title}
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Enter the unlock code.
              </p>
              
              <form onSubmit={handleUnlockSubmit}>
                <Input
                  label="Unlock Code"
                  value={unlockCode}
                  onChange={(e) => setUnlockCode(e.target.value.toUpperCase())}
                  placeholder="Enter code"
                  error={unlockError}
                  autoFocus
                />
                
                <div className="flex gap-3 mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCloseModal}
                    fullWidth
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    loading={unlockLoading}
                    disabled={!unlockCode}
                    fullWidth
                  >
                    Unlock Phase
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}